import React, { useMemo, useState, useEffect } from 'react';
import { useBranchStock } from '../../hooks/useBranchStock';
import { useProductCosts } from '../../hooks/useProductCosts';
import { useToast } from '../../hooks/useToast';
import { functions } from '../../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { costsService } from '../../services/costs.service';
import DashboardHeader from './DashboardHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, MapPin, Save, RefreshCw, AlertCircle } from 'lucide-react';
import { formatCurrency, cn } from '../../lib/utils';

import { startOfMonth, endOfMonth, format } from 'date-fns';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from 'recharts';
import { Switch } from "@/components/ui/switch";
import { ArrowUpDown, ArrowUp, ArrowDown, BarChart3, X } from 'lucide-react';
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

// "TIENDA GREEN VAPE & SMOKE SHOP PLAZA CAROLINA" → "PLAZA CAROLINA"
const shortBranchName = (name) =>
    name.replace(/(TIENDA\s+)?GREEN VAPE & SMOKE SHOP/gi, '').replace(/\s+/g, ' ').trim() || name;

export default function ProductCostsScreen() {
    // --- State ---
    const [selectedBranch, setSelectedBranch] = useState('ALL'); // Default to ALL
    const [availableBranches, setAvailableBranches] = useState([]); // Will be loaded from API
    const [branchesLoading, setBranchesLoading] = useState(false);
    const [allBranchesCosts, setAllBranchesCosts] = useState({}); // { branchName: { productId: {cost, updatedAt} } }
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('ALL');
    const [filterBrand, setFilterBrand] = useState('ALL');
    const [onlyWithStock, setOnlyWithStock] = useState(false);
    const [sortBy, setSortBy] = useState('totalValue-desc'); // name, stock-asc, stock-desc, cost-desc, totalValue-desc
    const [editingId, setEditingId] = useState(null);
    const [tempCost, setTempCost] = useState('');
    const [initializingCosts, setInitializingCosts] = useState(false);

    // --- Hooks ---
    const { toast, toasts } = useToast();
    const { stockByBranch, loading: stockLoading, metadata: stockMetadata, getBranchNames, getStockForBranch } = useBranchStock();
    const [syncingStock, setSyncingStock] = useState(false);

    // Descarga el stock actual de DynamiaERP (también corre solo cada día a las 3 a.m.)
    const handleSyncStock = async () => {
        setSyncingStock(true);
        try {
            const fn = httpsCallable(functions, 'syncCompleteInventory', { timeout: 540000 });
            const result = await fn();
            const s = result.data?.summary;
            toast({ title: 'Stock actualizado', description: s ? `${s.totalItems} productos en ${s.branches?.length ?? 0} sucursales` : 'Listo' });
        } catch (error) {
            toast({ title: 'Error al actualizar stock', description: error.message, variant: 'destructive' });
        } finally {
            setSyncingStock(false);
        }
    };
    const { costs, loading: costsLoading, error: costsError, fetchCosts, updateProductCost } = useProductCosts();

    // --- Effects ---

    // 1. Load Branches from RTDB (DynamiaERP sync data)
    // getBranchNames es una función nueva en cada render: no puede ir en las
    // dependencias (causaba un bucle infinito de renders + cargas de costos).
    // Solo se actualiza la lista si los nombres realmente cambiaron.
    useEffect(() => {
        if (stockByBranch && !stockLoading) {
            const branchNames = Object.keys(stockByBranch).sort();
            setAvailableBranches((prev) => (prev.join('|') === branchNames.join('|') ? prev : branchNames));
            setBranchesLoading(false);
        } else if (stockLoading) {
            setBranchesLoading(true);
        }
    }, [stockByBranch, stockLoading]);

    // 2. Load Costs when Branch changes
    useEffect(() => {
        const loadCostsForBranch = async () => {
            if (selectedBranch === 'ALL') {
                // Load costs for ALL branches
                console.log('[ProductCostsScreen] Loading costs for ALL branches...');
                const costsPromises = availableBranches.map(async (branch) => {
                    try {
                        const branchCosts = await costsService.getCosts(branch);
                        return { branch, costs: branchCosts };
                    } catch (error) {
                        console.error(`Error loading costs for ${branch}:`, error);
                        return { branch, costs: {} };
                    }
                });

                const results = await Promise.all(costsPromises);
                const allCosts = {};
                results.forEach(({ branch, costs }) => {
                    allCosts[branch] = costs;
                });
                setAllBranchesCosts(allCosts);
                console.log('[ProductCostsScreen] ✅ Loaded costs for all branches:', allCosts);
            } else if (selectedBranch) {
                // Load costs for specific branch
                fetchCosts(selectedBranch);
            }
        };

        loadCostsForBranch();
    }, [selectedBranch, availableBranches, fetchCosts]);

    // 3. Handle Permission/Network Errors
    useEffect(() => {
        if (costsError) {
            if (costsError.includes('Permission denied')) {
                toast({
                    title: 'Error de Permisos',
                    description: 'No tienes permiso para ver los costos. Contacta al administrador para actualizar las reglas de seguridad.',
                    variant: 'destructive',
                    duration: 10000
                });
            } else {
                toast({
                    title: 'Error cargando costos',
                    description: costsError,
                    variant: 'destructive'
                });
            }
        }
    }, [costsError]);

    // --- Derived Data ---

    // Extract Categories and Brands from Stock (DynamiaERP doesn't have these, so simplified)
    const { categories, brands } = useMemo(() => {
        return {
            categories: ['ALL', 'General'],
            brands: ['ALL', 'N/A']
        };
    }, []);

    // Merge Stock from RTDB + Costs
    const mergedData = useMemo(() => {
        if (!stockByBranch || stockLoading) return [];

        let productsMap = {}; // { productId: { ...productData } }

        if (selectedBranch === 'ALL') {
            // Combine all products from all branches
            Object.entries(stockByBranch).forEach(([branchName, products]) => {
                Object.entries(products).forEach(([productId, productData]) => {
                    if (!productsMap[productId]) {
                        productsMap[productId] = {
                            id: productId,
                            name: productData.itemName || 'Producto sin nombre',
                            sku: productData.itemCodigo || '',
                            category: 'General',
                            brand: 'N/A',
                            stock: 0,
                            branchStocks: {}, // { branchName: stock }
                            branchCosts: {}, // { branchName: cost }
                            totalCostValue: 0
                        };
                    }

                    // Add stock and cost for this branch
                    productsMap[productId].branchStocks[branchName] = productData.cantidad || 0;
                    productsMap[productId].branchCosts[branchName] = productData.costo || 0;
                    productsMap[productId].stock += productData.cantidad || 0;
                    productsMap[productId].totalCostValue += (productData.cantidad || 0) * (productData.costo || 0);
                });
            });

            // Calculate average cost for ALL mode
            Object.values(productsMap).forEach(product => {
                const costs = Object.values(product.branchCosts);
                product.unitCost = costs.length > 0
                    ? costs.reduce((sum, c) => sum + c, 0) / costs.length
                    : 0;
            });

        } else {
            // Specific branch
            const branchProducts = getStockForBranch(selectedBranch) || {};

            Object.entries(branchProducts).forEach(([productId, productData]) => {
                productsMap[productId] = {
                    id: productId,
                    name: productData.itemName || 'Producto sin nombre',
                    sku: productData.itemCodigo || '',
                    category: 'General',
                    brand: 'N/A',
                    stock: productData.cantidad || 0,
                    unitCost: productData.costo || 0,
                    totalCostValue: (productData.cantidad || 0) * (productData.costo || 0)
                };
            });
        }

        let data = Object.values(productsMap);

        // Filter: Search
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            data = data.filter(item =>
                item.name.toLowerCase().includes(lower) ||
                item.sku?.toLowerCase().includes(lower)
            );
        }

        // Filter: Category (simplified for DynamiaERP)
        if (filterCategory !== 'ALL' && filterCategory !== 'General') {
            data = [];
        }

        // Filter: Brand (simplified for DynamiaERP)
        if (filterBrand !== 'ALL' && filterBrand !== 'N/A') {
            data = [];
        }

        // Filter: Stock
        if (onlyWithStock) {
            data = data.filter(item => item.stock > 0);
        }

        // Sort
        data.sort((a, b) => {
            switch (sortBy) {
                case 'name': return a.name.localeCompare(b.name);
                case 'stock-asc': return a.stock - b.stock;
                case 'stock-desc': return b.stock - a.stock;
                case 'cost-desc': return b.unitCost - a.unitCost;
                case 'totalValue-desc': return (b.totalCostValue || 0) - (a.totalCostValue || 0);
                default: return 0;
            }
        });

        return data;
    }, [stockByBranch, stockLoading, selectedBranch, searchTerm, filterCategory, filterBrand, onlyWithStock, sortBy, getStockForBranch]);

    // Stats
    const stats = useMemo(() => {
        const totalValue = mergedData.reduce((acc, item) => acc + (item.totalCostValue || 0), 0);
        const zeroCostCount = mergedData.filter(item => !item.unitCost || item.unitCost === 0).length;
        const totalStock = mergedData.reduce((acc, item) => acc + (item.stock || 0), 0);

        // Count products with custom costs (from RTDB) vs default costs (from inventory)
        let customCostsCount = 0;
        if (selectedBranch !== 'ALL') {
            customCostsCount = Object.keys(costs).length;
        }

        console.log('[ProductCostsScreen] Stats Debug:', {
            selectedBranch,
            totalProducts: mergedData.length,
            customCostsCount,
            allBranchesCosts: selectedBranch === 'ALL' ? Object.keys(allBranchesCosts).map(b => `${b}: ${Object.keys(allBranchesCosts[b] || {}).length} costs`) : 'N/A',
            totalValue,
            zeroCostCount
        });


        // Chart Data: Top 5 Categories by Value
        const categoryMap = {};
        mergedData.forEach(item => {
            const val = item.totalCostValue || 0;
            if (val > 0) {
                categoryMap[item.category] = (categoryMap[item.category] || 0) + val;
            }
        });
        const categoryChartData = Object.entries(categoryMap)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 5);

        // Chart Data: Value Distribution (Stock vs No Stock Value?) No, maybe Cost Ranges or Active vs Inactive
        // Lets do Top 5 Products by Value instead
        const topProductsChartData = [...mergedData]
            .sort((a, b) => (b.totalCostValue || 0) - (a.totalCostValue || 0))
            .slice(0, 5)
            .map(item => ({ name: item.name.substring(0, 15) + '...', value: item.totalCostValue || 0 }));

        return { totalValue, zeroCostCount, count: mergedData.length, totalStock, categoryChartData, topProductsChartData };
    }, [mergedData]);

    // --- Handlers ---

    const handleEdit = (item) => {
        setEditingId(item.id);
        setTempCost(item.unitCost?.toString() || '');
    };

    const handleSave = async (item) => {
        try {
            await updateProductCost(selectedBranch, String(item.id), tempCost);
            setEditingId(null);
            toast({
                title: 'Costo actualizado',
                description: `Nuevo costo para ${item.name}: $${tempCost}`,
                variant: 'success'
            });
        } catch (error) {
            toast({
                title: 'Error al guardar',
                description: 'No se pudo actualizar el costo',
                variant: 'destructive'
            });
        }
    };

    const handleKeyDown = (e, item) => {
        if (e.key === 'Enter') handleSave(item);
        if (e.key === 'Escape') setEditingId(null);
    };

    const handleInitializeCosts = async () => {
        if (selectedBranch === 'ALL') {
            toast({
                title: 'Selecciona una sucursal',
                description: 'Debes seleccionar una sucursal específica para inicializar sus costos',
                variant: 'warning'
            });
            return;
        }

        setInitializingCosts(true);
        try {
            console.log(`[ProductCostsScreen] Initializing costs for branch: ${selectedBranch}`);
            const initializeBranchCostsFn = httpsCallable(functions, 'initializeBranchCosts');
            const result = await initializeBranchCostsFn({
                branch: selectedBranch,
                overwrite: false // Don't overwrite existing costs
            });

            if (result.data.success) {
                toast({
                    title: '✅ Costos inicializados',
                    description: `Se inicializaron ${result.data.initializedCount} productos en ${selectedBranch}. ${result.data.skippedCount} ya tenían costos.`,
                    variant: 'success'
                });

                // Reload costs for this branch
                await fetchCosts(selectedBranch, true);
            }
        } catch (error) {
            console.error('[ProductCostsScreen] Error initializing costs:', error);
            toast({
                title: '❌ Error al inicializar',
                description: error.message || 'No se pudieron inicializar los costos',
                variant: 'destructive'
            });
        } finally {
            setInitializingCosts(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-950 text-white px-4 py-5 md:p-8 space-y-4 md:space-y-6">
            <DashboardHeader
                activeTab="costs"
                title="Gestión de Costos"
                subtitle={stockMetadata?.lastSync
                    ? `Stock actualizado: ${format(new Date(stockMetadata.lastSync), 'dd/MM/yyyy HH:mm')}`
                    : 'Administra los costos por sucursal y valora tu inventario'}
                actions={
                    <Button onClick={handleSyncStock} disabled={syncingStock} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                        {syncingStock ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        {syncingStock ? 'Actualizando stock…' : 'Actualizar stock'}
                    </Button>
                }
            />

            {/* Controls */}
            <Card className="bg-white/5 border-white/10">
                <CardContent className="p-3 sm:p-4 space-y-3">

                    {/* Branch Tabs */}
                    <div className="flex items-center gap-1 overflow-x-auto scrollbar-none -mx-1 px-1">
                        <div className="flex items-center px-2 text-slate-400 border-r border-white/10 mr-1 shrink-0">
                            <MapPin className="w-4 h-4 text-slate-400" />
                        </div>

                        {/* "Todas" Button */}
                        <Button
                            variant={selectedBranch === 'ALL' ? 'default' : 'ghost'}
                            onClick={() => setSelectedBranch('ALL')}
                            className={cn(
                                "h-8 px-3 text-xs font-medium rounded-md whitespace-nowrap transition-all shrink-0",
                                selectedBranch === 'ALL'
                                    ? "bg-blue-600 text-white shadow-sm shadow-blue-500/20"
                                    : "text-slate-400 hover:text-white hover:bg-white/5"
                            )}
                        >
                            Todas las Sedes
                        </Button>

                        {availableBranches.map(branch => (
                            <Button
                                key={branch}
                                variant={selectedBranch === branch ? 'default' : 'ghost'}
                                onClick={() => setSelectedBranch(branch)}
                                className={cn(
                                    "h-8 px-3 text-xs font-medium rounded-md whitespace-nowrap transition-all shrink-0",
                                    selectedBranch === branch
                                        ? "bg-blue-600 text-white shadow-sm shadow-blue-500/20"
                                        : "text-slate-400 hover:text-white hover:bg-white/5"
                                )}
                            >
                                {shortBranchName(branch)}
                            </Button>
                        ))}

                        {/* Initialize Costs Button */}
                        {selectedBranch !== 'ALL' && (
                            <div className="h-4 w-px bg-white/10 mx-2" />
                        )}
                        {selectedBranch !== 'ALL' && (
                            <Button
                                variant="outline"
                                onClick={handleInitializeCosts}
                                disabled={initializingCosts}
                                className={cn(
                                    "h-8 px-3 text-xs font-medium rounded-md whitespace-nowrap transition-all shrink-0",
                                    "bg-green-600/10 border-green-500/30 text-green-400 hover:bg-green-600/20 hover:border-green-500/50"
                                )}
                            >
                                {initializingCosts ? (
                                    <>
                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                        Inicializando...
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="w-3 h-3 mr-1" />
                                        Inicializar Costos
                                    </>
                                )}
                            </Button>
                        )}
                    </div>

                    {/* Filters Row */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 w-full md:w-auto">
                        {/* Stock Filter */}
                        <div className="flex items-center gap-2">
                            <Switch
                                id="stock-filter"
                                checked={onlyWithStock}
                                onCheckedChange={setOnlyWithStock}
                            />
                            <Label htmlFor="stock-filter" className="text-xs text-white/70 whitespace-nowrap">Solo con stock</Label>
                        </div>

                        {/* Sort Filter */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-white/50 uppercase font-bold shrink-0">Ordenar:</span>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="bg-black/20 border border-white/10 rounded-md text-sm px-3 py-1.5 text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="totalValue-desc">Mayor Valor Total</option>
                                <option value="cost-desc">Mayor Costo</option>
                                <option value="stock-desc">Mayor Stock</option>
                                <option value="stock-asc">Menor Stock</option>
                                <option value="name">Nombre (A-Z)</option>
                            </select>
                        </div>

                        {/* Category Filter */}
                        <div className="flex items-center gap-2 overflow-x-auto max-w-[150px]">
                            <span className="text-xs text-white/50 uppercase font-bold shrink-0">Cat:</span>
                            <select
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                                className="bg-black/20 border border-white/10 rounded-md text-sm px-3 py-1.5 text-white focus:outline-none focus:border-blue-500 w-full"
                            >
                                {categories.map(cat => (
                                    <option key={cat} value={cat} className="bg-slate-900">{cat === 'ALL' ? 'Todas' : cat}</option>
                                ))}
                            </select>
                        </div>

                        {/* Brand Filter */}
                        <div className="flex items-center gap-2 overflow-x-auto max-w-[150px]">
                            <span className="text-xs text-white/50 uppercase font-bold shrink-0">Marca:</span>
                            <select
                                value={filterBrand}
                                onChange={(e) => setFilterBrand(e.target.value)}
                                className="bg-black/20 border border-white/10 rounded-md text-sm px-3 py-1.5 text-white focus:outline-none focus:border-blue-500 w-full"
                            >
                                {brands.map(brand => (
                                    <option key={brand} value={brand} className="bg-slate-900">{brand === 'ALL' ? 'Todas' : brand}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                </CardContent>
            </Card>

            {/* Stats Cards & Actions */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-white/5 border-white/10 backdrop-blur">
                    <CardContent className="p-4">
                        <p className="text-xs text-white/50 uppercase mb-1">Valor Total</p>
                        <div className="text-2xl font-bold text-green-400">{formatCurrency(stats.totalValue)}</div>
                        <p className="text-[10px] text-white/40 mt-1 truncate" title={`Basado en Stock Global * Costo ${selectedBranch}`}>
                            Stock Global * Costo {selectedBranch}
                        </p>
                    </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10 backdrop-blur">
                    <CardContent className="p-4">
                        <p className="text-xs text-white/50 uppercase mb-1">Stock Global</p>
                        <div className="text-2xl font-bold text-blue-200">{stats.totalStock}</div>
                        <p className="text-[10px] text-white/40 mt-1">
                            Unidades en todas las sucursales
                        </p>
                    </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10 backdrop-blur">
                    <CardContent className="p-4">
                        <p className="text-xs text-white/50 uppercase mb-1">Productos</p>
                        <div className="text-2xl font-bold text-blue-400">{stats.count}</div>
                        <p className="text-[10px] text-white/40 mt-1">
                            {onlyWithStock ? 'Con stock > 0' : 'Total catálogo'}
                        </p>
                    </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10 backdrop-blur">
                    <CardContent className="p-4 flex flex-col justify-between h-full gap-2">
                        <div>
                            <p className="text-xs text-white/50 uppercase mb-1">Sin Costo</p>
                            <div className={`text-2xl font-bold ${stats.zeroCostCount > 0 ? 'text-red-400' : 'text-white'}`}>{stats.zeroCostCount}</div>
                        </div>

                        <Dialog>
                            <DialogTrigger asChild>
                                <Button variant="outline" className="w-full bg-blue-600/20 hover:bg-blue-600/40 text-blue-200 border-blue-500/30">
                                    <BarChart3 className="w-4 h-4 mr-2" />
                                    Ver Gráficos
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle>Análisis Gráfico de Costos - {selectedBranch}</DialogTitle>
                                    <DialogDescription className="text-slate-400">
                                        Distribución del valor de inventario por categoría y productos principales.
                                    </DialogDescription>
                                </DialogHeader>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                                    {/* Chart 1: Categories */}
                                    <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                                        <h3 className="text-sm font-medium text-white/80 mb-4">Top 5 Categorías por Valor</h3>
                                        <div className="h-[300px] w-full">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={stats.categoryChartData} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                                                    <XAxis type="number" hide />
                                                    <YAxis dataKey="name" type="category" width={120} tick={{ fill: 'white', fontSize: 10 }} interval={0} />
                                                    <Tooltip
                                                        contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }}
                                                        itemStyle={{ color: '#4ade80' }}
                                                        formatter={(value) => formatCurrency(value)}
                                                    />
                                                    <Bar dataKey="value" fill="#4ade80" radius={[0, 4, 4, 0]} barSize={24} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>

                                    {/* Chart 2: Top Products */}
                                    <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                                        <h3 className="text-sm font-medium text-white/80 mb-4">Top 5 Productos por Valor</h3>
                                        <div className="h-[300px] w-full">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={stats.topProductsChartData} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                                                    <XAxis type="number" hide />
                                                    <YAxis dataKey="name" type="category" width={120} tick={{ fill: 'white', fontSize: 10 }} interval={0} />
                                                    <Tooltip
                                                        contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }}
                                                        itemStyle={{ color: '#60a5fa' }}
                                                        formatter={(value) => formatCurrency(value)}
                                                    />
                                                    <Bar dataKey="value" fill="#60a5fa" radius={[0, 4, 4, 0]} barSize={24} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </CardContent>
                </Card>
            </div>

            {/* Table */}
            <Card className="bg-transparent border-white/10">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader className="bg-white/5 hover:bg-white/5">
                                <TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead className="text-white/70">Producto</TableHead>
                                    <TableHead className="text-white/70">Marca</TableHead>
                                    <TableHead className="text-white/70">SKU</TableHead>
                                    <TableHead className="text-white/70">Categoría</TableHead>
                                    <TableHead className="text-right text-white/70">Stock</TableHead>
                                    <TableHead className="text-right text-white/70 w-[150px]">Costo Unit.</TableHead>
                                    <TableHead className="text-right text-white/70">Valor Total</TableHead>
                                    <TableHead className="w-[50px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody className="text-white">
                                {stockLoading || costsLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="h-24 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                                                <p className="text-xs text-white/50">Cargando inventario desde DynamiaERP...</p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : mergedData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="h-24 text-center text-white/50">
                                            No se encontraron productos.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    mergedData.map((item) => (
                                        <TableRow key={item.id} className="border-white/10 hover:bg-white/5">
                                            <TableCell className="font-medium max-w-[300px] truncate" title={item.name}>
                                                {item.name}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="border-white/10 bg-white/5 text-white/60 text-[10px] font-normal">
                                                    {item.brand || 'N/A'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-xs font-mono text-white/60">{item.sku}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="border-white/10 bg-white/5 text-white/60 text-[10px] font-normal">
                                                    {item.category}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-blue-300">
                                                {item.stock}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {editingId === item.id ? (
                                                    <div className="flex items-center gap-1 justify-end">
                                                        <Input
                                                            autoFocus
                                                            className="h-8 w-24 text-right bg-black/40 border-blue-500/50 focus-visible:ring-0 text-white"
                                                            value={tempCost}
                                                            onChange={(e) => setTempCost(e.target.value)}
                                                            onKeyDown={(e) => handleKeyDown(e, item)}
                                                            onBlur={() => handleSave(item)}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="cursor-pointer hover:text-blue-400 transition-colors flex items-center justify-end gap-2 group"
                                                        onClick={() => handleEdit(item)}
                                                    >
                                                        {selectedBranch !== 'ALL' && costs[String(item.id)] && (
                                                            <Badge variant="outline" className="border-green-500/30 bg-green-500/10 text-green-400 text-[9px] font-normal px-1 py-0">
                                                                Custom
                                                            </Badge>
                                                        )}
                                                        <span className={!item.unitCost ? 'text-red-400 font-bold' : ''}>
                                                            {item.unitCost ? formatCurrency(item.unitCost) : '$0.00'}
                                                        </span>
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right font-bold text-green-400">
                                                {formatCurrency(item.totalCostValue)}
                                            </TableCell>
                                            <TableCell>
                                                {editingId === item.id && (
                                                    <Button size="icon" variant="ghost" className="h-6 w-6 text-green-400 hover:text-green-300 hover:bg-green-500/20" onMouseDown={(e) => e.preventDefault()}>
                                                        <Save className="w-3 h-3" />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
            {/* Simple Toast Renderer */}
            {
                toasts.length > 0 && (
                    <div className="fixed bottom-4 right-4 z-[99999] flex flex-col gap-2">
                        {toasts.map(t => (
                            <div
                                key={t.id}
                                className={cn(
                                    "border px-4 py-3 rounded-md shadow-lg backdrop-blur-md min-w-[300px] animate-in slide-in-from-right-full",
                                    t.variant === 'destructive'
                                        ? "bg-red-950/90 border-red-500/50 text-red-200"
                                        : "bg-slate-900/90 border-green-500/50 text-green-200"
                                )}
                            >
                                <p className="font-bold text-sm">{t.title}</p>
                                {t.description && <p className="text-xs opacity-90 mt-1">{t.description}</p>}
                            </div>
                        ))}
                    </div>
                )
            }
        </div >
    );
}
